import { useQuery } from '@tanstack/react-query';

import { PromotionRoomType } from '~/components/Common/Forms/RatePlanForm/types';

import ReservationService from '~/services/ReservationService';

const PAGE_SIZE = 100;

// A rate plan reaches its room types through room_rates — no dedicated
// endpoint for this, so it's the properties response grouped by rate plan.
export function groupRoomTypesByRatePlan(properties: Array<App.Property>): Record<string, Array<PromotionRoomType>> {
  const roomTypesByRatePlan: Record<string, Map<string, PromotionRoomType>> = {};

  properties.forEach((property) => {
    (property.room_types || []).forEach((roomType) => {
      (roomType.room_rates || []).forEach((roomRate) => {
        const ratePlanId = roomRate.rate_plan?.id;
        if (!ratePlanId) {
          return;
        }
        const ratePlanRoomTypes = roomTypesByRatePlan[ratePlanId] ?? new Map();
        ratePlanRoomTypes.set(roomType.id, { id: roomType.id, name: roomType.name });
        roomTypesByRatePlan[ratePlanId] = ratePlanRoomTypes;
      });
    });
  });

  return Object.fromEntries(
    Object.entries(roomTypesByRatePlan).map(([ratePlanId, roomTypes]) => [ratePlanId, Array.from(roomTypes.values())]),
  );
}

export async function fetchAllProperties(vendorId: string): Promise<Array<App.Property>> {
  const properties: Array<App.Property> = [];
  let page = 1;

  while (true) {
    const response = await ReservationService.getProperties(vendorId, PAGE_SIZE, page, true);

    // An empty page ends pagination regardless of what `total` claims,
    // so a page-size cap or a mid-pagination deletion can't spin this forever.
    if (!response.result?.length) {
      break;
    }
    properties.push(...response.result);

    if (properties.length >= (response.total ?? properties.length)) {
      break;
    }
    page += 1;
  }

  return properties;
}

export const roomTypesByRatePlanQueryKeys = {
  all: ['rate-plans', 'room-types-by-rate-plan'] as const,
  vendor: (vendorId: string | undefined) => [...roomTypesByRatePlanQueryKeys.all, vendorId] as const,
};

// A rate plan still being created has no room rates, so there's nothing to fetch.
export function useRoomTypesByRatePlan(vendorId: string | undefined, ratePlanId: string | undefined) {
  return useQuery({
    queryKey: roomTypesByRatePlanQueryKeys.vendor(vendorId),
    queryFn: async () => groupRoomTypesByRatePlan(await fetchAllProperties(vendorId)),
    enabled: !!vendorId && !!ratePlanId,
  });
}
